import { useState } from "react";
import axios from "axios";
import { FadeIn } from "./FadeIn";
import { SITE, API } from "@/config/site";
import { ArrowRight, CheckCircle2, Mail, Phone, MapPin, FileDigit } from "lucide-react";

export const About = () => (
  <section id="about" className="bg-white border-b border-gray-200 scroll-mt-20" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
      <FadeIn className="lg:col-span-5">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">About</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="about-heading">
          About Westline Technologies
        </h2>
      </FadeIn>
      <FadeIn delay={0.1} className="lg:col-span-7 space-y-5 text-base md:text-lg text-zinc-600 leading-relaxed">
        <p>
          Westline Technologies is an Australian business focused on modernising the operational systems behind
          established companies.
        </p>
        <div className="bg-sage-pale border-l-2 border-forest p-6 rounded-r-md">
          <p className="text-sm text-zinc-500">We work from a simple principle:</p>
          <p className="mt-1.5 font-heading font-semibold text-charcoal leading-snug">
            Technology should reduce the amount of effort required to operate a business, not create another
            layer of administration.
          </p>
        </div>
        <p>
          We focus on practical commercial outcomes: reducing repetitive work, improving visibility, centralising
          information and creating systems capable of supporting continued growth.
        </p>
      </FadeIn>
    </div>
  </section>
);

const INDUSTRY_OPTIONS = [
  "Manufacturing", "Workshop / Fabrication", "Powder Coating", "Mining Services", "Industrial Services",
  "Trades", "Construction Services", "Automotive / Mechanical", "Warehousing", "Commercial Services", "Other",
];
const EMPLOYEE_OPTIONS = ["1–4", "5–9", "10–19", "20–49", "50+"];

const inputCls =
  "w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-sm text-charcoal placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-forest/25 focus:border-forest transition";

const Field = ({ label, children, span = false }) => (
  <label className={`block ${span ? "sm:col-span-2" : ""}`}>
    <span className="block text-[12px] font-semibold text-charcoal mb-1.5 tracking-wide">{label}</span>
    {children}
  </label>
);

const EMPTY = {
  name: "", business_name: "", email: "", phone: "", industry: "", employees: "", website: "",
  current_systems: "", management_time: "", manual_processes: "", improve_most: "", additional_info: "",
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again, or contact us directly.");
    }
  };

  return (
    <section id="contact" className="bg-charcoal scroll-mt-20" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-14">
        <FadeIn className="lg:col-span-5">
          <p className="text-[11px] font-semibold tracking-[0.28em] text-sage uppercase mb-5">Contact</p>
          <h2 className="font-heading font-bold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="contact-heading">
            Tell us how your business currently works.
          </h2>
          <p className="mt-5 text-base md:text-lg text-zinc-400 leading-relaxed">
            You do not need to know what software you need.
          </p>
          <p className="mt-2 font-heading font-semibold text-white text-lg md:text-xl">
            Start by telling us where your business is losing time.
          </p>
          <div className="mt-10 space-y-4 text-sm text-zinc-400" data-testid="contact-details">
            <p className="font-heading font-bold tracking-[0.18em] text-white text-sm">{SITE.name.toUpperCase()}</p>
            <p className="flex items-center gap-3"><MapPin size={15} className="text-sage shrink-0" />{SITE.location}</p>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-white transition-colors duration-200" data-testid="contact-email-link"><Mail size={15} className="text-sage shrink-0" />Email: {SITE.email}</a>
            <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 hover:text-white transition-colors duration-200" data-testid="contact-phone-link"><Phone size={15} className="text-sage shrink-0" />Phone: {SITE.phone}</a>
            {SITE.abn && <p className="flex items-center gap-3"><FileDigit size={15} className="text-sage shrink-0" />ABN: {SITE.abn}</p>}
          </div>
        </FadeIn>
        <FadeIn delay={0.12} className="lg:col-span-7">
          {status === "sent" ? (
            <div className="h-full min-h-[320px] border border-[#2A463A] bg-forest-deep rounded-lg p-10 flex flex-col items-start justify-center" data-testid="enquiry-success-message">
              <CheckCircle2 size={36} className="text-sage-light" strokeWidth={1.5} />
              <p className="mt-5 font-heading font-semibold text-white text-xl leading-snug">
                Thanks. Westline Technologies has received your enquiry and will be in contact.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="enquiry-form">
              <Field label="Name *">
                <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="enquiry-name-input" placeholder="Your name" />
              </Field>
              <Field label="Business Name">
                <input className={inputCls} value={form.business_name} onChange={set("business_name")} data-testid="enquiry-business-input" placeholder="Business name" />
              </Field>
              <Field label="Email *">
                <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="enquiry-email-input" placeholder="you@business.com.au" />
              </Field>
              <Field label="Phone">
                <input type="tel" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="enquiry-phone-input" placeholder="Phone number" />
              </Field>
              <Field label="Industry">
                <select className={inputCls} value={form.industry} onChange={set("industry")} data-testid="enquiry-industry-select">
                  <option value="">Select an industry</option>
                  {INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Approximate Number of Employees">
                <select className={inputCls} value={form.employees} onChange={set("employees")} data-testid="enquiry-employees-select">
                  <option value="">Select a range</option>
                  {EMPLOYEE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Website" span>
                <input className={inputCls} value={form.website} onChange={set("website")} data-testid="enquiry-website-input" placeholder="https://" />
              </Field>
              <Field label="What systems do you currently use?" span>
                <textarea rows={3} className={inputCls} value={form.current_systems} onChange={set("current_systems")} data-testid="enquiry-systems-input" placeholder="e.g. spreadsheets, Xero, paper job cards, a mix of software…" />
              </Field>
              <Field label="What takes up too much management time?" span>
                <textarea rows={3} className={inputCls} value={form.management_time} onChange={set("management_time")} data-testid="enquiry-management-time-input" />
              </Field>
              <Field label="What processes are still manual?" span>
                <textarea rows={3} className={inputCls} value={form.manual_processes} onChange={set("manual_processes")} data-testid="enquiry-manual-processes-input" />
              </Field>
              <Field label="What would you most like to improve?" span>
                <textarea rows={3} className={inputCls} value={form.improve_most} onChange={set("improve_most")} data-testid="enquiry-improve-input" />
              </Field>
              <Field label="Additional Information" span>
                <textarea rows={3} className={inputCls} value={form.additional_info} onChange={set("additional_info")} data-testid="enquiry-additional-input" />
              </Field>
              {error && <p className="sm:col-span-2 text-sm text-red-300" data-testid="enquiry-error-message">{error}</p>}
              <div className="sm:col-span-2 pt-2">
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex items-center gap-2 bg-sage text-charcoal font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-sage-light transition-colors duration-200 disabled:opacity-60"
                  data-testid="enquiry-submit-button"
                >
                  {status === "sending" ? "Sending…" : "Discuss My Business"}
                  <ArrowRight size={15} />
                </button>
                <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed max-w-lg">
                  Your information is used only to respond to your enquiry. See our Privacy Policy for details.
                </p>
              </div>
            </form>
          )}
        </FadeIn>
      </div>
    </section>
  );
};
